import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../CartContext';
import { getProductsByCategory, getCategories, getB2BCategories, mediaUrl } from '../api';
import { categories as fallbackCategories } from '../data';

export default function CategoryProductsPage() {
  const { categoryName, categoryId } = useParams();
  const navigate = useNavigate();
  const { addToCart, userType } = useCart();

  const [products, setProducts]     = useState([]);
  const [categories, setCategories] = useState(fallbackCategories);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [quantities, setQuantities] = useState({});
  const [added, setAdded]           = useState({});

  const decodedName = decodeURIComponent(categoryName);

  // If B2B mode is active, send user to the matching wholesale category page
  useEffect(() => {
    if (userType !== 'b2b') return;
    let cancelled = false;
    getB2BCategories()
      .then(res => {
        if (cancelled) return;
        const cats = res.categories || [];
        const match = cats.find(c => c.name.toLowerCase() === decodedName.toLowerCase());
        if (match) {
          navigate(`/b2b/products/${encodeURIComponent(match.name)}/${match.id}`, { replace: true });
        } else {
          navigate('/b2b', { replace: true });
        }
      })
      .catch(() => {
        if (!cancelled) navigate('/b2b', { replace: true });
      });
    return () => { cancelled = true; };
  }, [userType, decodedName, navigate]);

  // Load all categories for sidebar (B2C only)
  useEffect(() => {
    if (userType === 'b2b') return;
    getCategories()
      .then(res => {
        if (res.success && res.categories?.length > 0) {
          setCategories(res.categories.map(c => ({ id: c.id, name: c.name, image: c.image_url })));
        }
      })
      .catch(() => {});
  }, [userType]);

  // Load products for current category (B2C only)
  useEffect(() => {
    if (userType === 'b2b') return;
    setLoading(true);
    setError('');
    setProducts([]);
    getProductsByCategory(categoryId)
      .then(res => {
        if (res.success) setProducts(res.products || []);
        else setError(res.message || 'Failed to load products.');
      })
      .catch(() => setError('Unable to connect to server.'))
      .finally(() => setLoading(false));
  }, [categoryId, userType]);

  const handleQty = (id, val) => {
    const q = Math.max(1, parseInt(val) || 1);
    setQuantities(prev => ({ ...prev, [id]: q }));
  };

  const handleAdd = (product) => {
    const qty = quantities[product.id] || 1;
    addToCart({
      id:       product.id,
      name:     product.name,
      price:    parseFloat(product.price),
      image:    product.image_url,
      category: product.category,
      unit:     product.unit || '',
      isB2B:    false,
    }, qty);
    setAdded(prev => ({ ...prev, [product.id]: true }));
    setTimeout(() => setAdded(prev => ({ ...prev, [product.id]: false })), 1400);
  };

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh' }}>

      {/* ── Breadcrumb bar ── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '10px 0' }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', color: '#6b7280' }}>
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16a34a', fontWeight: 600, fontSize: '0.8rem', padding: 0 }}>Home</button>
          <span>›</span>
          <span style={{ color: '#111827', fontWeight: 500 }}>{decodedName}</span>
        </div>
      </div>

      {/* ── Category pills ── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '10px 0' }}>
        <div className="container">
          <div className="km-cat-pills" style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '0', msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
            <style>{`.km-cat-pills::-webkit-scrollbar { display: none; }`}</style>
            {categories.map(cat => {
              const active = String(cat.id) === String(categoryId);
              return (
                <button
                  key={cat.id}
                  onClick={() => navigate(`/category/${encodeURIComponent(cat.name)}/${cat.id}`)}
                  style={{
                    flexShrink: 0,
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '5px 12px',
                    borderRadius: '20px',
                    border: `1.5px solid ${active ? '#16a34a' : '#e5e7eb'}`,
                    background: active ? '#f0fdf4' : '#fff',
                    color: active ? '#16a34a' : '#374151',
                    fontWeight: active ? 700 : 500,
                    fontSize: '0.78rem',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={e => { if (!active) { e.currentTarget.style.borderColor = '#16a34a'; e.currentTarget.style.color = '#16a34a'; } }}
                  onMouseLeave={e => { if (!active) { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; } }}
                >
                  {cat.image && (
                    <img src={cat.image} alt="" style={{ width: 18, height: 18, objectFit: 'cover', borderRadius: '50%' }} onError={e => e.target.style.display = 'none'} />
                  )}
                  {cat.name}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Page heading ── */}
      <div className="container" style={{ padding: '20px 1rem 0' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
          <h1 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#111827', margin: 0 }}>{decodedName}</h1>
          {!loading && products.length > 0 && (
            <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{products.length} products</span>
          )}
        </div>
      </div>

      {/* ── Content ── */}
      <div className="container" style={{ padding: '16px 1rem 3rem' }}>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '4rem' }}>
            <div style={{ width: 36, height: 36, border: '3px solid #16a34a', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite', margin: '0 auto 12px' }} />
            <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>Loading products...</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Error / empty */}
        {!loading && (error || products.length === 0) && (
          <div style={{ textAlign: 'center', padding: '4rem 1rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🛒</div>
            <p style={{ fontWeight: 600, color: '#374151', marginBottom: '4px' }}>{error || 'No products found'}</p>
            <button onClick={() => navigate('/')} style={{ marginTop: '12px', padding: '8px 20px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>
              Back to Home
            </button>
          </div>
        )}

        {/* Product grid */}
        {!loading && products.length > 0 && (
          <>
            <style>{`
              .km-product-grid {
                display: grid;
                grid-template-columns: repeat(5, 1fr);
                gap: 14px;
                margin-top: 4px;
              }
              @media (max-width: 1024px) { .km-product-grid { grid-template-columns: repeat(4, 1fr); } }
              @media (max-width: 768px)  { .km-product-grid { grid-template-columns: repeat(3, 1fr); } }
              @media (max-width: 480px)  { .km-product-grid { grid-template-columns: repeat(2, 1fr); } }
              
              /* Fix + Add button text alignment and font size on mobile */
              @media (max-width: 768px) {
                .b2c-add-btn-fix {
                  text-align: left !important;
                  justify-content: flex-start !important;
                  font-size: 0.65rem !important;
                }
              }
              @media (max-width: 480px) {
                .b2c-add-btn-fix {
                  text-align: left !important;
                  justify-content: flex-start !important;
                  font-size: 0.6rem !important;
                }
              }
            `}</style>
          <div className="km-product-grid">
            {products.map(product => {
              const qty      = quantities[product.id] || 1;
              const price    = parseFloat(product.price);
              const orig     = product.original_price ? parseFloat(product.original_price) : null;
              const discount = orig ? Math.round(((orig - price) / orig) * 100) : null;
              const isAdded  = added[product.id];

              return (
                <div key={product.id} style={{
                  background: '#fff',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                  overflow: 'hidden',
                  position: 'relative',
                  transition: 'box-shadow 0.2s, transform 0.2s',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {/* Added notification */}
                  {isAdded && (
                    <div style={{ position: 'absolute', bottom: '8px', left: '8px', right: '8px', background: '#16a34a', color: 'white', borderRadius: '6px', padding: '5px 10px', fontSize: '0.72rem', fontWeight: 600, textAlign: 'center', zIndex: 10, boxShadow: '0 2px 8px rgba(22,163,74,0.4)' }}>
                      ✓ Added to cart
                    </div>
                  )}

                  {/* Badge */}
                  {(product.badge || discount) && (
                    <div style={{
                      position: 'absolute', top: '8px', left: '8px',
                      background: product.badge_color || '#dc2626',
                      color: 'white', padding: '2px 7px', borderRadius: '20px',
                      fontSize: '0.65rem', fontWeight: 700, zIndex: 1,
                    }}>
                      {product.badge || `${discount}% OFF`}
                    </div>
                  )}

                  {/* Image */}
                  <div style={{ background: '#f8fafc', height: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }}>
                    {product.image_url
                      ? <img src={mediaUrl(product.image_url)} alt={product.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />
                      : <span style={{ fontSize: '2.5rem' }}>🛒</span>
                    }
                  </div>

                  {/* Info */}
                  <div style={{ padding: '10px 10px 12px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <p style={{ fontSize: '0.8rem', fontWeight: 700, color: '#111827', lineHeight: 1.3, marginBottom: '3px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {product.name}{product.unit ? ` (${product.unit})` : ''}
                    </p>

                    {/* Price row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '8px' }}>
                      <span style={{ fontSize: '0.82rem', fontWeight: 800, color: '#16a34a' }}>₹{price}{product.unit ? `/${product.unit}` : ''}</span>
                      {orig && <span style={{ fontSize: '0.68rem', textDecoration: 'line-through', color: '#9ca3af' }}>₹{orig}</span>}
                    </div>

                    {/* Qty + Add */}
                    <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden', flexShrink: 0 }}>
                        <button onClick={() => handleQty(product.id, qty - 1)} style={{ width: 26, height: 28, background: '#f9fafb', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700, color: '#374151' }}>−</button>
                        <span style={{ minWidth: '22px', textAlign: 'center', fontSize: '0.8rem', fontWeight: 600 }}>{qty}</span>
                        <button onClick={() => handleQty(product.id, qty + 1)} style={{ width: 26, height: 28, background: '#f9fafb', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700, color: '#374151' }}>+</button>
                      </div>
                      <button
                        onClick={() => handleAdd(product)}
                        style={{ padding: '6px 14px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'background 0.15s' }} className="b2c-add-btn-fix"
                        onMouseEnter={e => e.currentTarget.style.background = '#15803d'}
                        onMouseLeave={e => e.currentTarget.style.background = '#16a34a'}
                      >
                        + Add
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>
    </div>
  );
}
